<template>
  <section class="relative pb-12 pt-8 z-1">
    <div class="container">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-[30px]">
        <div class="xl:col-span-8 lg:col-span-7">
          <div class="p-6 shadow dark:shadow-gray-700 rounded-md">
            <h5 class="text-xl font-semibold" v-if="company?.name">{{ company.name }}</h5>

            <div class="inline-flex align-center gap-1 mt-1" v-if="company?.address || company?.location?.name">
              <i class="uil uil-location-point text-base"></i>
              {{ company.address || company.location.name }}
            </div>

            <p class="text-slate-700 dark:text-slate-400 mt-8" v-if="company?.description">{{ company.description }}</p>
          </div>

          <div v-if="company?.images && company?.images.length > 0" class="mt-8">
            <h5 class="text-xl font-semibold mb-4">Images:</h5>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div v-for="(image, index) in company.images" :key="index" class="relative">
                <img :src="image.url" alt="img" class="w-full h-auto object-contain rounded-md shadow dark:shadow-gray-700">
              </div>
            </div>
          </div>

          <div v-if="companyJobs && companyJobs.length !== 0" class="mt-8">
            <h5 class="text-xl font-semibold mb-4">Jobs:</h5>

            <div class="flex gap-6 overflow-x-auto w-full pb-8 pt-1 px-1">
              <div
                v-for="(job, index) in companyJobs"
                :key="index"
                class="shadow dark:shadow-gray-700 rounded-md w-[80%] sm:w-[calc(50%-13px)] flex-shrink-0 flex flex-col"
              >
                <div class="p-5 flex flex-col gap-2">
                  <router-link
                    :to="{ name: 'JobDetail', params: { slug: job.slug } }"
                    class="text-lg"
                  >
                    {{ job.title }}
                  </router-link>

                  <div class="flex items-center gap-2" v-if="job?.postDate">
                    <span class="text-gradient">
                      <i class="uil uil-clock"></i>
                    </span>

                    <span class="text-slate-400">{{ job.postDate }}</span>
                  </div>

                  <div class="flex items-center gap-3 mt-1">
                    <div class="flex bg-green-thin py-1 px-2 rounded" v-if="job?.type?.name">
                      <span class="text-xs leading-none font-bold text-gradient">{{ job.type.name }}</span>
                    </div>
                  </div>
                </div>

                <hr class="border-t border-slate-200 dark:border-gray-700 mt-auto" />

                <div class="p-5 flex md:items-center gap-3 flex-col md:flex-row">
                  <img class="flex-shrink-0 w-12 h-12 object-cover rounded-md border dark:border-gray-800" :src="job?.company?.logoUrl || placeholderLogo" alt="">

                  <div class="flex flex-col overflow-hidden">
                    <span class="font-semibold truncate" v-if="job?.company?.name">{{ job.company.name }}</span>
                    <span class="text-sm text-slate-400 truncate" v-if="job?.address || job?.location?.name">{{ job.address || job.location.name }}</span>
                  </div>

                  <a
                    v-if="job?.externalUrl"
                    :href="job.externalUrl"
                    class="btn rounded-md text-white w-full md:w-auto bg-gradient text-nowrap ml-auto"
                  >
                    Job ansehen
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="xl:col-span-4 lg:col-span-5">
          <div class="bg-slate-50 dark:bg-slate-800 rounded-md shadow dark:shadow-gray-700 p-6">
            <div
              v-if="company?.address || company?.location?.name || company?.latitude && company?.longitude"
              class="w-full leading-[0] mb-4"
            >
              <iframe
                v-if="company.address"
                class="w-full h-[350px] border-0 rounded-md shadow dark:shadow-gray-700"
                loading="lazy"
                allowfullscreen
                referrerpolicy="no-referrer-when-downgrade"
                :src="`https://www.google.com/maps?q=${company.address}&output=embed`">
              </iframe>
              <iframe
                v-else-if="!company.address && company.location?.name"
                class="w-full h-[350px] border-0 rounded-md shadow dark:shadow-gray-700"
                loading="lazy"
                allowfullscreen
                referrerpolicy="no-referrer-when-downgrade"
                :src="`https://www.google.com/maps?q=${company.location.name}&output=embed`">
              </iframe>
              <iframe
                v-else-if="!company.address && !company.location?.name && company.latitude && company.longitude"
                class="w-full h-[350px] border-0 rounded-md shadow dark:shadow-gray-700"
                loading="lazy"
                allowfullscreen
                referrerpolicy="no-referrer-when-downgrade"
                :src="`https://www.google.com/maps?q=${company.latitude},${company.longitude}&output=embed`">
              </iframe>
            </div>

            <ul class="list-none flex flex-col gap-2">
              <li class="flex justify-between gap-2" v-if="company?.foundedYear">
                <span class="text-slate-400 font-medium">Founded:</span>
                <span class="font-medium text-end truncate">{{ company.foundedYear }}</span>
              </li>

              <li class="flex justify-between gap-2" v-if="company?.category?.name">
                <span class="text-slate-400 font-medium">Category:</span>
                <span class="font-medium text-end truncate">{{ company.category.name }}</span>
              </li>

              <li class="flex justify-between gap-2" v-if="company?.address || company?.location?.name">
                <span class="text-slate-400 font-medium">Headquarters:</span>
                <span class="font-medium text-end truncate">{{ company.address || company.location.name }}</span>
              </li>

              <li class="flex justify-between gap-2" v-if="company?.size">
                <span class="text-slate-400 font-medium">Number of employees:</span>
                <span class="font-medium text-end truncate">{{ company.size }}</span>
              </li>

              <li class="flex justify-between gap-2" v-if="company?.website">
                <span class="text-slate-400 font-medium">Website:</span>
                <span class="font-medium text-end truncate">{{ company.website }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import placeholderAvatar from '@/assets/images/job/placeholder/job8.png';

export default {
  props: {
    company: {
      type: Object,
      required: true
    },
    companyJobs: {
      type: Array,
      required: false
    }
  },
  data() {
    return {
      placeholderLogo: placeholderAvatar,
    }
  },
};
</script>

<style scoped></style>
